import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ShoppingCart, AlertTriangle, TrendingUp, Clock, DollarSign, Package } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urgencyConfig = {
  critical: { label: 'Critical', class: 'bg-red-100 text-red-700 border-red-300', icon: '🔴' },
  high: { label: 'High', class: 'bg-orange-100 text-orange-700 border-orange-300', icon: '🟠' },
  medium: { label: 'Medium', class: 'bg-amber-100 text-amber-700 border-amber-300', icon: '🟡' },
  low: { label: 'Low', class: 'bg-slate-100 text-slate-700 border-slate-300', icon: '⚪' },
};

const ReorderSuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createPoDialogOpen, setCreatePoDialogOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [orderQuantity, setOrderQuantity] = useState(0);
  const [creating, setCreating] = useState(false);
  const { canEdit } = useAuth();
  const { formatCurrency } = useSettings();

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/reorder-suggestions`);
      setSuggestions(response.data);
    } catch (error) {
      toast.error('Failed to load reorder suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreatePo = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setOrderQuantity(suggestion.suggested_quantity);
    setCreatePoDialogOpen(true);
  };

  const handleCreatePo = async () => {
    if (!selectedSuggestion || orderQuantity <= 0) return;
    
    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/reorder-suggestions/${selectedSuggestion.part_id}/create-po`,
        null,
        { params: { quantity: orderQuantity } }
      );
      toast.success(`Purchase Order ${response.data.order_number} created!`);
      setCreatePoDialogOpen(false);
      setSelectedSuggestion(null);
      fetchSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create purchase order');
    } finally {
      setCreating(false);
    }
  };

  const totalEstimatedCost = suggestions.reduce((sum, s) => sum + s.estimated_cost, 0);
  const criticalCount = suggestions.filter(s => s.urgency === 'critical').length;
  const highCount = suggestions.filter(s => s.urgency === 'high').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reorder-suggestions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Reorder Suggestions</h1>
          <p className="text-slate-500 mt-1">AI-powered recommendations based on stock levels and order history</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                <Package className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Total Items</p>
                <p className="text-2xl font-bold">{suggestions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-2 shadow-sm rounded-sm ${criticalCount > 0 ? 'border-red-500 bg-red-50' : 'border-slate-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${criticalCount > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                <AlertTriangle className={`w-5 h-5 ${criticalCount > 0 ? 'text-red-600' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Critical</p>
                <p className={`text-2xl font-bold ${criticalCount > 0 ? 'text-red-600' : ''}`}>{criticalCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-2 shadow-sm rounded-sm ${highCount > 0 ? 'border-orange-400 bg-orange-50' : 'border-slate-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${highCount > 0 ? 'bg-orange-100' : 'bg-slate-100'}`}>
                <TrendingUp className={`w-5 h-5 ${highCount > 0 ? 'text-orange-600' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">High Priority</p>
                <p className={`text-2xl font-bold ${highCount > 0 ? 'text-orange-600' : ''}`}>{highCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Est. Cost</p>
                <p className="text-xl font-bold text-emerald-600">{formatCurrency(totalEstimatedCost)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Suggestions Table */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardHeader className="border-b bg-slate-50">
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Suggested Reorders
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {suggestions.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Urgency</TableHead>
                  <TableHead>Part</TableHead>
                  <TableHead className="text-right">Current</TableHead>
                  <TableHead className="text-right">Min</TableHead>
                  <TableHead className="text-right">Suggested Qty</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead className="text-right">Est. Cost</TableHead>
                  <TableHead>Last Order</TableHead>
                  {canEdit() && <TableHead className="w-32"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {suggestions.map(suggestion => (
                  <TableRow 
                    key={suggestion.part_id} 
                    className={suggestion.urgency === 'critical' ? 'bg-red-50' : ''}
                    data-testid={`suggestion-row-${suggestion.part_id}`}
                  >
                    <TableCell>
                      <Badge className={`rounded-sm ${urgencyConfig[suggestion.urgency]?.class}`}>
                        {urgencyConfig[suggestion.urgency]?.icon} {urgencyConfig[suggestion.urgency]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-mono font-semibold">{suggestion.part_reference}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[200px]">{suggestion.part_description}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono font-semibold ${
                        suggestion.current_quantity === 0 ? 'text-red-600' : 
                        suggestion.current_quantity <= suggestion.minimum_quantity ? 'text-amber-600' : ''
                      }`}>
                        {suggestion.current_quantity}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-slate-500">
                      {suggestion.minimum_quantity}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono font-bold text-blue-600">{suggestion.suggested_quantity}</span>
                      {suggestion.avg_order_quantity > 0 && (
                        <p className="text-xs text-slate-400">Avg: {suggestion.avg_order_quantity}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {suggestion.supplier_name || <span className="text-slate-400">No supplier</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-emerald-600">
                      {formatCurrency(suggestion.estimated_cost)}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {suggestion.last_order_date ? (
                        <div>
                          <p>{new Date(suggestion.last_order_date).toLocaleDateString()}</p>
                          <p className="text-xs text-slate-400">{suggestion.days_since_last_order} days ago</p>
                        </div>
                      ) : (
                        <span className="text-slate-400">Never</span>
                      )}
                    </TableCell>
                    {canEdit() && (
                      <TableCell>
                        <Button
                          size="sm"
                          onClick={() => handleOpenCreatePo(suggestion)}
                          disabled={!suggestion.supplier_id}
                          className="rounded-sm bg-blue-600 hover:bg-blue-700"
                          data-testid={`create-po-btn-${suggestion.part_id}`}
                        >
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          Create PO
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <TrendingUp className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No reorder suggestions</p>
              <p className="text-sm">All parts are above minimum stock levels</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create PO Dialog */}
      <Dialog open={createPoDialogOpen} onOpenChange={setCreatePoDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-po-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">Create Purchase Order</DialogTitle>
            <DialogDescription>
              Create a PO for {selectedSuggestion?.part_reference}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSuggestion && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-sm border">
                <p className="font-mono font-semibold">{selectedSuggestion.part_reference}</p>
                <p className="text-sm text-slate-600">{selectedSuggestion.part_description}</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span>Current: <strong className="text-red-600">{selectedSuggestion.current_quantity}</strong></span>
                  <span>Minimum: <strong>{selectedSuggestion.minimum_quantity}</strong></span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Order Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={orderQuantity}
                  onChange={(e) => setOrderQuantity(parseInt(e.target.value) || 0)}
                  className="rounded-sm"
                  data-testid="order-quantity-input"
                />
                <p className="text-xs text-slate-500">
                  Suggested: {selectedSuggestion.suggested_quantity} | Average: {selectedSuggestion.avg_order_quantity}
                </p>
              </div>

              <div className="p-3 bg-emerald-50 rounded-sm border border-emerald-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-emerald-700">Estimated Total</span>
                  <span className="font-bold text-emerald-700 text-lg">
                    {formatCurrency(orderQuantity * (selectedSuggestion.estimated_cost / selectedSuggestion.suggested_quantity))}
                  </span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePoDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePo} 
              disabled={creating || orderQuantity <= 0}
              className="bg-blue-600 hover:bg-blue-700 rounded-sm"
              data-testid="confirm-create-po-btn"
            >
              {creating ? 'Creating...' : 'Create PO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReorderSuggestions;
